import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { apiRequest, API_BASE_URL } from "../api/client";

function About({ onOpenAuth, onOpenMenu }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [about, setAbout] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      const res = await apiRequest("api/about_us");
      if (!alive) return;
      if (!res.ok) {
        setError("Failed to load About Us content.");
        setAbout(null);
      } else {
        setAbout(res.data || null);
      }
      setLoading(false);
    })().catch(() => {
      if (!alive) return;
      setError("Failed to load About Us content.");
      setAbout(null);
      setLoading(false);
    });

    return () => {
      alive = false;
    };
  }, []);

  const attachmentUrl = useMemo(() => {
    const raw = about?.attachment_url;
    if (!raw) return "";
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    return `${API_BASE_URL.replace(/\/$/, "")}${raw.startsWith("/") ? "" : "/"}${raw}`;
  }, [about?.attachment_url]);

  const title = about?.title?.trim?.() ? about.title : "About Us";
  const description = about?.description?.trim?.() ? about.description : "";

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A] flex flex-col">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto flex w-full max-w-7xl flex-1 items-stretch px-4 py-6 md:px-6 md:py-8">
        <section className="flex w-full min-h-full items-center rounded-3xl border border-[#E6EAF0] bg-white p-6 shadow-sm md:p-10">
          <div className="mx-auto grid w-full max-w-6xl items-start gap-8 md:grid-cols-[200px_1fr]">
            {attachmentUrl ? (
              <img
                src={attachmentUrl}
                alt={title}
                className="h-36 w-full rounded-2xl object-cover md:h-44"
              />
            ) : (
              <div className="h-36 w-full rounded-2xl border border-[#E6EAF0] bg-[#F5F7FB] md:h-44" />
            )}

            <div>
              <h1 className="text-[28px] font-bold tracking-tight text-[#24303A] md:text-[28px]">
                {title}
              </h1>
              {loading ? (
                <p className="mt-4 text-[14px] leading-relaxed text-[#6E7B86] md:text-base">
                  Loading...
                </p>
              ) : error ? (
                <div className="mt-4">
                  <p className="text-[14px] leading-relaxed text-red-600 md:text-base">{error}</p>
                  <button
                    type="button"
                    onClick={() => {
                      setLoading(true);
                      setError("");
                      apiRequest("api/about_us")
                        .then((res) => {
                          if (!res.ok) {
                            setError("Failed to load About Us content.");
                            setAbout(null);
                          } else {
                            setAbout(res.data || null);
                          }
                        })
                        .catch(() => {
                          setError("Failed to load About Us content.");
                          setAbout(null);
                        })
                        .finally(() => setLoading(false));
                    }}
                    className="mt-3 rounded-md bg-[#7352C7] px-4 py-2 text-[14px] font-semibold text-white hover:bg-[#5f43a8]"
                  >
                    Retry
                  </button>
                </div>
              ) : description ? (
                <div
                  className="mt-4 whitespace-pre-line text-[14px] leading-relaxed text-[#6E7B86] md:text-base"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              ) : (
                <p className="mt-4 text-[14px] leading-relaxed text-[#6E7B86] md:text-base">
                  No About Us content has been added yet.
                </p>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default About;
